import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import clsx from 'clsx'
import type { ReactElement } from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { Paper } from '~/components/Global/Blocks/Paper/Paper'
import { ConversationList } from '~/domains/messaging/components/ConversationList/ConversationList'
import { MessageThread } from '~/domains/messaging/components/MessageThread/MessageThread'
import { ApplicantContext } from '~/domains/messaging/components/MessagingContext/ApplicantContext'
import { ContactContext } from '~/domains/messaging/components/MessagingContext/ContactContext'
import {
  contextEmpty,
  contextWrapper
} from '~/domains/messaging/components/MessagingContext/MessagingContext.css'
import { MessagingContextPanel } from '~/domains/messaging/components/MessagingContext/MessagingContextPanel'
import type { SelectedConversation } from '~/domains/messaging/types'
import { AsideActionContext } from '~/utils/contextes'
import useDocumentTitle from '~/utils/documentTitle'
import {
  emptyThread,
  messagingCol1,
  messagingCol2,
  messagingCol3,
  messagingCol3Collapsed,
  messagingCol3Expanded,
  messagingPage,
  messagingPageWrapper
} from './-components/MessagingPage.css'

const COLLAPSE_BREAKPOINT = 1200
const EXPAND_BREAKPOINT = 1700
const STORAGE_KEY = 'messagingContextCollapsed'

type MessagingSearchParams = {
  type?: 'applicant' | 'contact'
  id?: number
}

const MessagingPage = (): ReactElement => {
  useDocumentTitle('Messagerie')
  const navigate = useNavigate({ from: '/messaging/' })
  const { type, id } = useSearch({ from: '/_dashboard/messaging/' })
  const mainRef = useRef<HTMLDivElement>(null)
  const { clearAction } = useContext(AsideActionContext)

  const [contextCollapsed, setContextCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored !== null) return stored === 'true'
    return window.innerWidth < COLLAPSE_BREAKPOINT
  })
  const [actionOpen, setActionOpen] = useState(false)
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    () => window.innerWidth < EXPAND_BREAKPOINT
  )

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < COLLAPSE_BREAKPOINT) {
        setContextCollapsed(true)
      }
      setIsNarrowViewport(window.innerWidth < EXPAND_BREAKPOINT)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const toggleContext = () => {
    setContextCollapsed((prev) => {
      const next = !prev
      localStorage.setItem(STORAGE_KEY, String(next))
      return next
    })
  }

  const selected: SelectedConversation | null = type && id ? { type, id } : null

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the selected conversation changes
  useEffect(() => {
    setActionOpen(false)
    clearAction()
  }, [selected?.type, selected?.id])

  const handleSelect = (conversation: SelectedConversation) => {
    void navigate({
      search: { type: conversation.type, id: conversation.id }
    })
  }

  return (
    <Paper contentPadding={false} className={messagingPageWrapper}>
      <div ref={mainRef} className={messagingPage}>
        <div className={messagingCol1}>
          <ConversationList selected={selected} onSelect={handleSelect} />
        </div>

        <div className={messagingCol2}>
          {selected ? (
            <MessageThread conversation={selected} />
          ) : (
            <div className={emptyThread}>Sélectionnez une conversation</div>
          )}
        </div>

        <div
          className={clsx(
            messagingCol3,
            contextCollapsed
              ? messagingCol3Collapsed
              : actionOpen && isNarrowViewport && messagingCol3Expanded
          )}
        >
          {contextCollapsed ? (
            <MessagingContextPanel collapsed onToggle={toggleContext} />
          ) : !selected ? (
            <div className={contextWrapper}>
              <div className={contextEmpty}>Sélectionnez une conversation</div>
            </div>
          ) : selected.type === 'applicant' ? (
            <ApplicantContext
              id={selected.id}
              onToggle={toggleContext}
              onActionOpenChange={setActionOpen}
            />
          ) : (
            <ContactContext
              id={selected.id}
              onToggle={toggleContext}
              onActionOpenChange={setActionOpen}
            />
          )}
        </div>
      </div>
    </Paper>
  )
}

export const Route = createFileRoute('/_dashboard/messaging/')({
  component: MessagingPage,
  validateSearch: (search: Record<string, unknown>): MessagingSearchParams => {
    const type = search.type
    const id = Number(search.id)
    if (
      (type === 'applicant' || type === 'contact') &&
      Number.isFinite(id) &&
      id > 0
    ) {
      return { type, id }
    }
    return {}
  }
})
